import React from "react";
import { Star, ExternalLink } from "lucide-react";
import { RESTAURANT } from "@/lib/restaurant";

/**
 * Build the proper "write a review" URL.
 * If placeId is provided → direct Google review form.
 * Otherwise → fallback to search results so the user can find the business.
 */
export function getWriteReviewUrl() {
  if (RESTAURANT.google.placeId) {
    return `https://search.google.com/local/writereview?placeid=${RESTAURANT.google.placeId}`;
  }
  return RESTAURANT.google.writeReviewUrl;
}

export function getViewReviewsUrl() {
  if (RESTAURANT.google.placeId) {
    return `https://search.google.com/local/reviews?placeid=${RESTAURANT.google.placeId}`;
  }
  return RESTAURANT.google.profileUrl;
}

/** Small rating badge — for use in hero / info cards */
export function GoogleRatingBadge({ className = "" }) {
  const r = RESTAURANT.google.rating;
  return (
    <a
      href={getViewReviewsUrl()}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="google-rating-badge"
    >
      <span className="font-serif-display text-2xl">{r.toFixed(1)}</span>
      <Stars value={r} />
      <span className="text-xs opacity-75">({RESTAURANT.google.reviewCount})</span>
    </a>
  );
}

function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex">
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = i < Math.floor(value);
        const half = !filled && i < value;
        return (
          <Star
            key={i}
            size={size}
            className={filled || half ? "text-[#FFB300]" : "text-gray-300"}
            fill={filled ? "#FFB300" : half ? "url(#half)" : "none"}
          />
        );
      })}
    </span>
  );
}

/** Big section to drop on the home page */
export default function GoogleReviewsSection() {
  const r = RESTAURANT.google.rating;
  const reviews = [
    {
      name: "Carla M.",
      rating: 5,
      text: "Ambiance familiale, plats portugais authentiques et soirées festives le samedi. On s'est régalés et on a dansé jusque tard !",
    },
    {
      name: "Antoine D.",
      rating: 5,
      text: "Le bacalhau à brás est exceptionnel et l'accueil chaleureux. Je recommande sans hésiter pour les amateurs de cuisine portugaise.",
    },
    {
      name: "Maria F.",
      rating: 4,
      text: "Très bonne table, pastéis de nata délicieux. Cadre sympa pour un repas en famille. À refaire.",
    },
  ];

  return (
    <section className="py-24 bg-[#FDFBF7]" data-testid="google-reviews-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left — overall rating */}
          <div className="lg:col-span-4">
            <div className="eyebrow">Avaliações</div>
            <h2 className="text-4xl md:text-5xl mt-3 leading-tight">
              Ils nous <em className="italic text-[#C04E38]">adorent</em>
            </h2>
            <div className="mt-8 card-warm p-7">
              <div className="flex items-center gap-3 mb-3">
                <img alt="Google" className="w-7 h-7" src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><path fill='%234285F4' d='M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.5 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z'/><path fill='%2334A853' d='M6.3 14.7l7 5.1C15.1 16 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.5 4.1 29.6 2 24 2 15.7 2 8.6 6.6 6.3 14.7z'/><path fill='%23FBBC05' d='M24 46c5.5 0 10.4-1.9 14.1-5.1l-6.5-5.5c-2 1.4-4.6 2.2-7.6 2.2-6 0-10.7-3.1-12.3-7.5l-7 5.4C7.6 41.4 15.1 46 24 46z'/><path fill='%23EA4335' d='M44.5 20H24v8.5h11.8c-.6 1.7-1.6 3.2-3 4.4l6.5 5.5C42.9 35.1 46 30 46 24c0-1.3-.2-2.7-.5-4z'/></svg>"/>
                <span className="font-medium">Avis Google</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-6xl text-[#2C2B29]">{r.toFixed(1)}</span>
                <span className="text-[#5C5A56]">/ 5</span>
              </div>
              <Stars value={r} size={20} />
              <p className="text-sm text-[#5C5A56] mt-2">Basé sur {RESTAURANT.google.reviewCount} avis Google</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a
                  href={getWriteReviewUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary !py-2.5 !px-5 text-sm"
                  data-testid="leave-review-btn"
                >
                  Laisser un avis <ExternalLink size={14} className="ml-2"/>
                </a>
                <a
                  href={getViewReviewsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost !py-2.5 !px-5 text-sm"
                  data-testid="see-all-reviews-btn"
                >
                  Voir tous les avis
                </a>
              </div>
            </div>
          </div>

          {/* Right — testimonials */}
          <div className="lg:col-span-8 grid md:grid-cols-3 gap-5">
            {reviews.map((rv, i) => (
              <div key={i} className="card-warm p-6 flex flex-col" data-testid={`review-card-${i}`}>
                <Stars value={rv.rating} />
                <p className="text-[#2C2B29] mt-4 leading-relaxed flex-1 italic">"{rv.text}"</p>
                <div className="mt-5 pt-4 border-t border-[#E6E1D6] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#1F476B] text-white grid place-items-center font-serif-display text-sm">
                    {rv.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{rv.name}</div>
                    <div className="text-xs text-[#5C5A56]">via Google</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
